/**
 * The post-incident review — Step 54.
 *
 * Kai: *"a PIR should just be part of the MI ticket"*, so it is a tab on the
 * major incident rather than somewhere you navigate to. It is stored in its
 * own table because it has its own lifecycle — drafted days later, by someone
 * else, and PUBLISHED — but none of that is visible from here.
 *
 * **The timeline arrives pre-filled.** Reconstructing the night from memory a
 * week later is the step everybody skips, and skipping it is what makes a PIR
 * a paragraph of apology instead of a document. The server builds it from the
 * audit log — every status change, assignment and note, with times — and the
 * author edits it. `Reset from the record` is there because an author who
 * cleared the box must be able to get it back.
 *
 * **Publishing runs the shared gate**, previewed here and enforced on the
 * server by the same function: stood down first (a review of something still
 * running is a status update), and the three factual sections written. The
 * reflective ones stay optional — a gate nobody can satisfy on a Friday
 * evening produces no review at all.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileDown, Plus } from 'lucide-react';
import {
  PIR_SECTIONS,
  pirCompleteness,
  pirPublishGate,
  type PirDocConfig,
  type PirDraft,
  type TimelineEntry,
} from '@itsm/shared';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';
import { useUrlState } from '../../lib/urlState';
import { fullTime } from '../../lib/time';
import type { MajorIncident } from './OutageTab';
import { TimelineEditor } from './TimelineEditor';
import { DocumentEditor } from './DocumentEditor';
import { derivePirFacts } from './pirFacts';
import { ticketsApi } from './api';

interface Pir extends PirDraft {
  status: string;
  publishedAt: number | null;
  lastPublishedAt: number | null;
  updatedAt: number | null;
  timelineEntries: TimelineEntry[] | null;
  docConfig: PirDocConfig | null;
}

const BLANK: Record<string, string> = Object.fromEntries(PIR_SECTIONS.map((s) => [s.key, '']));

export function ReviewTab({ id, canEdit }: { id: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.tickets.pir(id),
    queryFn: () => apiFetch<{ entry: Pir | null; suggestions: TimelineEntry[] }>(`/api/tickets/${id}/pir`),
  });
  // The gate needs the stand-down, which lives on the outage record. Shared
  // query key, so opening this tab after the Outage tab costs nothing.
  const mi = useQuery({
    queryKey: qk.tickets.majorIncident(id),
    queryFn: () =>
      apiFetch<{ entry: (MajorIncident & { managerName?: string | null }) | null }>(
        `/api/tickets/${id}/major-incident`,
      ),
  });
  // The same inputs the document uses, through the same derivation — so what
  // the editor calls "derived" is exactly what would print.
  const ticket = useQuery({ queryKey: qk.tickets.detail(id), queryFn: () => ticketsApi.detail(id) });

  const [draft, setDraft] = useState<Record<string, string>>(BLANK);
  /**
   * What the form looked like when it opened — the only honest baseline for
   * "has anything actually changed?".
   *
   * It cannot be the STORED row, which is what this compared against first.
   * The timeline is pre-filled from the audit log when nothing has been
   * written, so on a fresh review `draft.timeline` is a page of text while
   * `stored.timeline` is null — the tab was dirty the instant it rendered,
   * and clicking onto it and away again asked to discard work nobody had done
   * (Kai). A pre-fill is a suggestion, not an edit.
   */
  const [baseline, setBaseline] = useState<Record<string, string>>(BLANK);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [timelineBase, setTimelineBase] = useState<TimelineEntry[]>([]);
  /**
   * Three jobs, three screens.
   *
   * They were one column: six rich-text editors, eighteen override rows and
   * the timeline, stacked. Kai: *"the editor looks awful and not user
   * friendly"*, *"it's very cluttered"*. Writing the narrative, curating the
   * timeline and deciding what the PDF prints are separate sittings, often by
   * different people, and putting them on one page made every one of them
   * harder to find.
   *
   * URL-backed like every other view in the app, so a colleague can be sent
   * straight to the section that matters.
   */
  const [section, setSection] = useUrlState('sec', 'write');
  const [docCfg, setDocCfg] = useState<PirDocConfig>({});
  const [docBase, setDocBase] = useState<PirDocConfig>({});
  // URL state on purpose: which EMPTY optional sections have been revealed
  // this sitting. There is nothing in them to lose on a refresh, and the ones
  // that have been written re-appear on their own because the render checks
  // for content — putting this in the address bar would put "I clicked a
  // button" in a link somebody shares.
  const [openOptional, setOpenOptional] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!q.data || loaded) return;
    const e = q.data.entry;
    const initial = {
      ...BLANK,
      ...Object.fromEntries(PIR_SECTIONS.map((s) => [s.key, (e?.[s.key] as string | null) ?? ''])),
    };
    setDraft(initial);
    setBaseline(initial);
    // Suggestions are NOT poured in — they sit behind a button in the editor.
    setTimeline(e?.timelineEntries ?? []);
    setTimelineBase(e?.timelineEntries ?? []);
    setDocCfg(e?.docConfig ?? {});
    setDocBase(e?.docConfig ?? {});
    setLoaded(true);
  }, [q.data, loaded]);

  const stored = q.data?.entry;
  const dirty =
    loaded &&
    (PIR_SECTIONS.some((s) => draft[s.key] !== baseline[s.key]) ||
      JSON.stringify(timeline) !== JSON.stringify(timelineBase) ||
      JSON.stringify(docCfg) !== JSON.stringify(docBase));
  const clearDirty = useDirty(dirty);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.tickets.pir(id) });
    void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
  };
  const save = useMutation({
    mutationFn: () =>
      apiSend(`/api/tickets/${id}/pir`, 'PUT', {
        ...draft,
        // Blank rows are half-typed entries, not content.
        timelineEntries: timeline.filter((e) => e.text.trim()),
        docConfig: docCfg,
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      // The saved draft IS the new baseline; without this the form stays
      // dirty against the values it just wrote.
      setBaseline(draft);
      setTimelineBase(timeline);
      setDocBase(docCfg);
      clearDirty();
      refresh();
    },
  });
  const publish = useMutation({
    mutationFn: () => apiSend(`/api/tickets/${id}/pir/publish`, 'POST'),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not publish.');
      toast.success(published ? 'Review republished.' : 'Review published.');
      refresh();
    },
  });

  if (q.isPending) return <Skeleton className="m-4 h-64" />;
  if (q.isError)
    return (
      <div className="m-4 space-y-2 rounded-lg border p-4">
        <p className="text-sm font-medium text-destructive">Could not load the review</p>
        <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
          Retry
        </Button>
      </div>
    );

  // Previewed against the DRAFT, so the reason updates as it is typed rather
  // than only after a save.
  const gate = pirPublishGate(draft as PirDraft, {
    stoodDownAt: mi.data?.entry?.stoodDownAt ?? null,
  });
  const done = pirCompleteness(draft as PirDraft);
  const published = stored?.status === 'published';
  /**
   * Republishing needs something to republish.
   *
   * Publish is enabled once the gate passes; Republish is not, because a
   * published review that has not changed since is already published and the
   * button would do nothing but move a timestamp (Kai: *"the republish button
   * shows active even if there's nothing to republish"*).
   *
   * Against `lastPublishedAt`, NOT `publishedAt`: publishing bumps
   * `updatedAt`, so comparing with the first publication date left the button
   * lit for ever after the first republish.
   */
  const revisedSincePublish =
    !!stored?.lastPublishedAt && !!stored?.updatedAt && stored.updatedAt > stored.lastPublishedAt;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mx-auto w-full max-w-3xl min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Post-incident review</h2>
          {published ? (
            <Badge variant="outline" className="border-emerald-500/40">
              Published {stored?.publishedAt ? fullTime(stored.publishedAt) : ''}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              Draft · {done}% written
            </Badge>
          )}
          <div className="flex-1" />
          {/* The stakeholder copy (Kai). A print-optimised page plus the
            browser's own Save-as-PDF, rather than shipping a PDF engine to
            render six paragraphs — and it picks up the tenant's branding for
            free because it IS the page. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(`/tickets/${id}/review/print`, '_blank', 'noopener')}
          >
            <FileDown className="h-4 w-4" />
            Export PDF
          </Button>
        </div>

        {/* One row of segments rather than three headings down a page: the
            sections are alternatives, not steps, and a tab bar says so. */}
        <div className="flex gap-1 rounded-md border p-0.5">
          {(
            [
              ['write', 'Write-up'],
              ['timeline', `Timeline${timeline.length ? ` · ${timeline.length}` : ''}`],
              ['document', 'Document'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSection(key)}
              className={cn(
                'flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                section === key
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {section === 'write' &&
          PIR_SECTIONS.filter(
            (sec) => sec.required || (draft[sec.key] ?? '').trim() || openOptional.includes(sec.key),
          ).map((sec) => (
            <div key={sec.key}>
              <label className="mb-1 flex items-baseline gap-2 text-xs font-medium text-muted-foreground">
                {sec.label}
                {sec.required && <span className="text-[10px] uppercase tracking-wide">required</span>}
              </label>
              {/* Shorter than the default 120: these are paragraphs, not
                  articles, and six boxes at full height turned a three-answer
                  form into a page and a half of empty space. They grow with
                  what is typed. */}
              <RichTextEditor
                value={draft[sec.key] ?? ''}
                onChange={(v) => setDraft((d) => ({ ...d, [sec.key]: v }))}
                minHeight={72}
              />
            </div>
          ))}

        {/* The optional sections start folded away. A review needs three
            things; offering six empty boxes made the required ones harder to
            see, and an empty "What went well" is not a prompt, it is noise. */}
        {section === 'write' && canEdit && (
          <div className="flex flex-wrap gap-1.5">
            {PIR_SECTIONS.filter(
              (sec) => !sec.required && !(draft[sec.key] ?? '').trim() && !openOptional.includes(sec.key),
            ).map((sec) => (
              <Button
                key={sec.key}
                size="sm"
                variant="outline"
                onClick={() => setOpenOptional((o) => [...o, sec.key])}
              >
                <Plus className="h-3.5 w-3.5" /> {sec.label}
              </Button>
            ))}
          </div>
        )}

        {section === 'document' && (
          <DocumentEditor
            config={docCfg}
            onChange={setDocCfg}
            derived={derivePirFacts({
              ticket: ticket.data?.ticket as never,
              mi: mi.data?.entry ?? null,
              slaClocks: ticket.data?.slaClocks ?? [],
            })}
            readOnly={!canEdit}
          />
        )}

        {section === 'timeline' && (
          <TimelineEditor
            entries={timeline}
            onChange={setTimeline}
            suggestions={q.data?.suggestions ?? []}
            readOnly={!canEdit}
          />
        )}

        {section === 'timeline' && (
          <p className="text-xs text-muted-foreground">
            Follow-up actions live on the Tasks tab, so each one has an owner and a status.
          </p>
        )}
      </div>
      {/* Docked, like the properties rail's Update (Kai). A review runs to
          several screens, and Publish at the very bottom of it is a scroll
          nobody should have to make twice. */}
      {canEdit && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-card px-4 py-2.5">
          {/* The reason is shown, not just a disabled button — "why can't I
              publish" should never need asking. */}
          {gate && !published && <span className="mr-auto text-xs text-muted-foreground">{gate}</span>}
          <Button variant="outline" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          {/* Republishing is allowed. A review gets corrected after it goes
              out — a wrong root cause, a missing action — and a document you
              cannot correct is one that stays wrong (Kai: *"I can't republish
              a review"*). The first publication date is kept; a later one is
              recorded as a revision. */}
          <Button
            disabled={!!gate || dirty || publish.isPending || (published && !revisedSincePublish)}
            title={
              dirty
                ? 'Save your changes first.'
                : published && !revisedSincePublish
                  ? 'Nothing has changed since it was published.'
                  : undefined
            }
            onClick={() => publish.mutate()}
          >
            {publish.isPending ? 'Publishing…' : published ? 'Republish' : 'Publish'}
          </Button>
        </div>
      )}
    </div>
  );
}
